import Backbone from 'backbone'
import _ from 'lodash'
import TreeStream from 'scoreboard/components/tree-stream'
import $ from 'jquery'
import paste from 'scoreboard/components/paste/paste'
import globals from 'scoreboard/components/globals'
import i18n from 'scoreboard/components/i18n'
import defaultPasteAllowed from 'scoreboard/components/paste/default-paste-allowed'
import loadingDots from 'scoreboard/components/loading-dots.hbs'
import constants from 'scoreboard/components/constants'
import newFolderAddType from 'scoreboard/components/new-folder-add-type'
import metadata from 'scoreboard/components/metadata'
import Finder from 'scoreboard/components/finder'
import sections from 'scoreboard/components/sections'
import { Transform } from 'stream'
import template from './gardener.hbs'
import expandCollapseButton from 'scoreboard/components/bottom-toolbar-expand-collapse-button.hbs'
import siprNodeTransform from 'scoreboard/components/sipr-node-transform'
import { ORG_HEADER_NAME } from 'scoreboard/components/permissions'
import { CHILD, SIBLING, ROOT } from 'scoreboard/components/add-types'
import unwrapModel from 'scoreboard/components/unwrap-model'
import config from 'scoreboard/components/config'

/**
 * ###################################
 * ######## Kalpa Tree Docs ##########
 * ###################################
 * @see https://github.com/SpiderStrategies/kalpa-tree
 */
import Tree from 'kalpa-tree'

/**
 * The gardener manages our kalpa-trees and places a finder above the tree and
 * an expand/collapse button below it.
 *
 * ### How to configure the appearance and functionality of the tree
 * To make the tree editable, pass in the `editable: true` initialization option
 *
 * @property {Tree} tree
 *
 * see _bindEvents for complete list
 *
 * @emits success
 * 	when the tree response is known to be successful
 * 	(might not yet have a node, listen to 'node' for that)
 *
 * @emits select
 * 	when the tree fires a 'select' event
 *
 * @emits node
 * 	when the tree fires a 'node' event
 *
 * @emits error
 * 	when the tree fires an 'error' event
 */
var Gardener = Backbone.View.extend(/** @lends Gardener.prototype */{

	className: 'gardener',

	// more trees are forests than not
	forest: true,

	events: {
		'click .expand-collapse': 'toggleCollapse',
		'click .clear': 'clearSelection'
	},

	expandable: _.constant(true),

	/**
	 * subclasses can assign N transform functions for each time a node is
	 * added to the tree (e.g. dataset-tree-gardener does an icon-transform)
	 */
	nodeTransforms: [],

	/**
	 * @constructs a new gardener and immediately begins streaming data.
	 *
	 * @param {Object} options
	 *
	 * @param {Object} [options.params] extra parameters to append to the end of
	 * the URL used to retrieve the nodes in the tree (see jQuery.param)
	 *
	 * @param {Function} options.contents A function to generate tree contents.
	 * passed the arguments: (selection, tree, transitions)
	 *
	 * @param {Object} options.streamOptions options for the stream that returns
	 * data from a request
	 *
	 * @param {Object} options.finderOptions options to pass to the finder
	 *
	 * @param {Object} [options.lightTree=false] true if we should be using a light styled tree
	 *
	 * @param {Boolean} [options.showClearButton=false] if set to true, will
	 * show a "Clear" button to clear the current selection for the tree (leaving
	 * nothing selected)
	 */
	initialize: function(options = {}) {
		this.options = _.cloneDeep(options)

		// stream the data right away if possible
		if (this.readyToStream(this.options.streamOptions)) {
			this._initializeStream(this.options.streamOptions)
		}

		this.initializeFinder(options)

		// indicates the tree starts out collapsed
		this.collapsed = true

		// don't bind to global events more than once
		this.listenTo(Backbone, 'add:domain-object', this._addDomainObject)
		this.listenTo(Backbone, 'edit:domain-object', this._editDomainObject)
		this.listenTo(Backbone, 'edit:domain-objects', this._editDomainObjects)
		this.listenTo(Backbone, 'delete:domain-object', this._deleteDomainObject)
		this.listenTo(Backbone, 'move:domain-object', this._moveDomainObject)
		this.listenTo(Backbone, 'copy:domain-object', this._copyDomainObject)
	},

	initializeFinder: function(options) {
		this.finder = new Finder(options.finderOptions)
		// this.tree isn't defined yet so be sure to reference it in a callback
		this.listenTo(this.finder, 'find', text => {
			this.searchText = text
			this._onceTreeRendered(() => {
				this.tree.search(this.searchText)
				this.trigger('search')
			})
		})
	},

	/**
	 * Renders the finder, tree and expand/collapse button
	 */
	render: function(options) {
		this.$el.html(template(this.options))

		// render the finder
		this.$('.finder').replaceWith(this.finder.el)
		this.finder.render()

		// show the tree if possible
		if (this.stream) {
			this.show(options)
		}

		// render the expand/collapse button
		this.expandable() && this.$('.bottom-toolbar').append(expandCollapseButton())

		if (this.options.showClearButton) {
			this.$('.bottom-toolbar').addClass('nav-or-tree-toolbar nav-bottom-toolbar is-editable')
				.append(`<button class="clear" type="button">
							<span class="label">
								<span>${i18n.get('common.clear')}</span>
							</span>
						</button>`)
		}

		this.insertLoadingDots()
		this.rendered = true

		return this
	},

	/**
	 * Shows the tree with the provided state.  Options has the following parameters:
	 *
	 * @param {Number|String} options.id the id of the object whose tree we
	 * wish to display (when orgId not specified)
	 *
	 * @param {Number} options.orgId the id of the organization of the tree we
	 * wish display (when id not specified)
	 *
	 * @param {String} [options.section=this.options.section] the section of
	 * tree to display (e.g. organizations, scorecards)
	 *
	 * @param {Boolean} [options.force=false]
	 *
	 * @param {Boolean} [options.editable] If not provided the former value will be applied.
	 */
	show: function (options = {}) {
		var compatibleSection = _.isUndefined(_.result(options, 'section')) ||
			this.options.section == options.section
		if (options.force) {
			compatibleSection = false
		}
		if (this.tree) {
			if (options.force) {
				// trigger success event when server located the tree we requested
				this.tree.once('node', this.trigger.bind(this, 'success'))
			}
			// If we're already showing the right section and the node is in the current
			// tree, just highlight the node
			if (options.id && compatibleSection) {
				// #25113 if the tree isn't in the body anymore, we need to reload it
				if (this.isTreeInBody()) {
					if (this.tree.get(options.id)) {
						this.tree.select(options.id, {silent: true, toggleOnSelect: false})
						return this
					}
				}
			}
		}

		// after switching trees, reset the button at the bottom to "expand all"
		// instead of "collapse all"
		this.toggleCollapse(true)

		this._updateOptions(options)

		if (this.rendered || !this.stream) {
			this._initializeStream(options)
		}

		this._showNewTree(options)

		return this
	},

	_showNewTree(options = {}) {
		// #14323 #14805 #17370 retain tree's editable state.  Be sure to load
		// whether the tree is editable first before the tree is potentially
		// re-drawn and loses it's editable state
		let editable = options.editable || this.editable()

		// Don't wipe out the current tree until we know we have another one
		this._renderTree()

		// #14323 #14805 #17370 retain tree's editable state
		if (options.force && editable) {
			this.tree.editable()
		}

		this.$('.tree').toggleClass('editable',
			// #55954 #55995 a falsey value for editable means the tree is not
			// editable
			editable == true)

		if (this.tree) {
			this.trigger('change:tree')
		}
	},

	/**
	 * Re-fetch the tree and render it.
	 */
	reload: function(stream) {
		this._initializeStream({}, stream)
		this._renderTree()
	},

	/**
	 * If the gardener is ready to request it's stream.
	 * @returns {boolean}
	 */
	readyToStream: function() {
		return true
	},

	/**
	 *
	 * @param {Object} [options] options to pass to the TreeStream
	 * @param {Stream} [stream] Can be used to provide a stream
	 * (e.g. from an error response already containing tree nodes)
	 */
	_initializeStream (options = {}, stream) {
		//stop previous stream (if any) to prevent race-conditions #23011
		_.invoke(this.stream, 'abort')
		this.stream = stream || this._stream(options)

		//notify listeners whenever a stream on a gardener is set. Garden listeners can then attached any
		//listeners to the stream whenever a stream is set/changes in the gardener
		this.trigger('stream', this.stream)
	},

	/**
	 * Executes a callback once all data for the tree has been loaded from the
	 * server.  The tree won't be fully rendered yet, so you may want to use
	 * _onceTreeRendered instead.
	 *
	 * @param {Function} callback will be called after the tree is loaded and
	 * passed the tree as an argument.
	 */
	_onceTreeLoaded: function(callback) {
		if (_.get(this.stream, 'finished')) {
			callback(this.tree)
		} else {
			let listened = false
			// #14300 only listen once because the tree could be reloaded in
			// the datasets section
			this.listenToOnce(this, 'end error', () => {
				// #34534 don't listen for both end and error!
				if (listened) return
				callback(this.tree)
				listened = true
			})
		}
	},

	/**
	 * Executes a callback once all data for the tree has been loaded from the
	 * server AND the tree had a chance to render.  If you don't need to wait
	 * for the tree to render, you can use _onceTreeLoaded instead.
	 */
	_onceTreeRendered(callback) {
		if (this.treeRendered) {
			callback()
		} else {
			this.once('rendered', callback)
		}
	},

	isTreeInBody: function() {
		return $('body')[0].contains(this.tree.el.node())
	},

	insertLoadingDots: function() {
		// #8237 be sure to clear any existing loading timeout that may be around!
		clearTimeout(this.loadingTimeout)
		this.loadingTimeout = setTimeout(function() {
			this.$('.tree').addClass('is-not-displayed').before(loadingDots())
		}.bind(this), constants.LOADING_TIME)
	},

	clearLoadingDots: function() {
		// if the loading dots aren't displayed yet, prevent them from being
		// displayed
		clearTimeout(this.loadingTimeout)
		// remove any loading dots that may be visible
		this.$('.loading-dots').remove()
		// show the tree
		this.$('.tree').removeClass('is-not-displayed')
	},

	/**
	 * When the first tree node is received, which we can extract the org from
	 * @param node
	 * @private
	 */
	_onFirstNode: function(node) {
		this.orgId = this.getOrgId()
		this.options.orgId = node.organizationId
		this.trigger('node', node)
	},

	/**
	 * Selects the item with the given ID in the tree.  Also handles the case
	 * where the tree is still loading and the given ID isn't present yet to
	 * select.
	 */
	select: function(id, options) {
		var gardener = this
		if (this.tree.get(id)) {
			this.tree.select(id, options)
		} else {
			this.tree.on('node', function(node) {
				if (node.id == id) {
					gardener.tree.select(id, options)
				}
			})
		}
	},

	_renderTree: function (treeOptions) {
		treeOptions = treeOptions || this._treeOptions()

		// note the old tree should no longer be referenced and automatically
		// garbage collected so the events we listen to here are fine
		this.tree = new Tree(treeOptions)
		this.treeRendered = false

		// in current kalpa-tree implementation, this creates this.tree.el so
		// it must go first
		this.tree.render()
		let tree = this.tree.el.node()
		$(tree).addClass(this.$('.tree-container').attr('class'))
		this.$('.tree-container').replaceWith(tree)

		this._bindEvents()

	},

	/**
	 * Returns an appropriate URL for retrieving the tree based on the given
	 * options, if possible.  If it's not possible to determine a URL yet,
	 * returns null.
	 */
	_url: function (opt) {
		if (opt.id || opt.id === 0) {
			return 'api/' + sections.toUrl(opt.section) + '/' + opt.id + '/tree'
		} else if (opt.orgId || opt.orgId === 0) {
			return 'api/organizations/' + opt.orgId + '/' + sections.toUrl(opt.section) + '/tree'
		} else {
			return null
		}
	},

	// generates the options to pass to the Tree
	_treeOptions: function() {
		var opts = {
			initialSelection: this.options.id || this.options.initialSelection,
			// NOTE: The tree icon is determined by this 'type' accessor unless a different icon is specified.
			// (see documents-gardener:_treeTransform() for an example)
			accessors: { icon: 'type' },
			forest: this.forest,
			stream: this.stream,
			droppable: defaultPasteAllowed,
			movable: function (node) {
				return node && !(this.tree.organizationHasTemplate && !!node.templateId) && _.isNil(node.syncId)
			}.bind(this)
		}

		// If a contents function is passed in to customize the look of the tree items,
		// include it in the tree options
		if (this.options.contents) {
			_.extend(opts, {
				contents: this.options.contents
			})
		}

		let self = this
		let _classNameTransform = new Transform({ objectMode: true })
		_classNameTransform._transform = function (node, enc, next) {
			self._treeNodeClassName(node)
			_.each(self.nodeTransforms, transformFn => transformFn(node))
			this.push(node)
			next()
		}
		opts.stream = opts.stream.pipe(_classNameTransform)

		return opts
	},

	_treeNodeClassName (node) {
		siprNodeTransform(node)
		return node
	},

	/**
	 * Creates a TreeStream to retrieve the tree
	 *
	 * @param {Object} [options] options to pass to the TreeStream
	 * @returns {Stream} the TreeStream
	 */
	_stream: function(options) {
		var url = this._url(this.options)
		if (this.options.params) {
			if (url.indexOf('?') === -1) {
				url += '?'
			} else {
				url += '&'
			}
			url += $.param(this.options.params)
		}
		var opts =_.defaults({}, options, {
			url: url
		})
		return new TreeStream(opts)
	},

	_updateOptions: function(options) {
		if (!options) {
			return
		}

		if (options.id) {
			delete this.options.orgId
		}
		// this stuff is needed because in the show method if we already have
		// an ID we will just select in the tree.  If someone then calls
		// show with an org ID we would ignore the request to show a different
		// tree
		if (options.orgId) {
			delete this.options.id
		}
		_.extend(this.options, options)
	},

	/**
	 * Gets or sets whether the tree is editable
	 */
	editable: function (editable) {
		// set whether the tree is editable, if appropriate
		if (this.tree && !_.isUndefined(editable) && editable != this.tree.isEditable()) {
			this.tree.editable(editable)
		}
		// always return whether the tree is now editable
		return this.tree && this.tree.isEditable()
	},

	_bindEvents: function() {
		// Listen to the stream success event because drilldowns depend on this
		// to render the nav view into the DOM, and it needs to happen before node
		// events are handled.
		this.stream.on('success', () => this.trigger('success'))
		this.stream.on('error', this.trigger.bind(this, 'error'))
		// i tried listening to the tree instead, but it didn't seem to work for
		// some reason
		this.stream.on('error', this.clearLoadingDots.bind(this))
		// important: the end event on the stream needs to be added AFTER the
		// tree has a chance to bind its end event.  otherwise the gardener
		// will emit an end event before the tree is truly finished (#12655)
		this.stream.on('end', this.trigger.bind(this, 'end'))

		this.stream.on('headers', function (headers) {
			this.tree.organizationHasTemplate = headers['organization-has-template'] == 'true'
			this.tree.organizationLabel = headers[constants.TREE_HEADER_ORGANIZATION_LABEL.toLowerCase()]
		}.bind(this))

		// always bind events to the tree, which was newly created
		this.tree.on('select', this.trigger.bind(this, 'select'))
		this.tree.on('error', this.trigger.bind(this, 'error'))
		// trigger success event when server located the tree we requested
		this.tree.once('node', node => this._onFirstNode(node))
		this.tree.on('move', this.move.bind(this))
		this.tree.on('rendered', () => {
			this.treeRendered = true
			this.trigger('rendered')
		})

		// #14997 provide auto selection of root if requested
		this._onceTreeRendered(() => {
			if (this.tree && this.options.autoSelectRoot && _.isNil(this.options.id)) {
				let root = this.tree.get()
				if (_.isArray(root)) {
					root = _.find(root, node => node.visible != false)
				}
				if (root && root.id &&
					// #25394 don't need to select the transient node
					root.id != this.tree.options.transientId) {

					//#33354 #33273 #32756 #31937
					//do not silence this selection as views may need to be
					//notified once the auto root has been selected.  to prevent
					//the "double load" see nav.js
					this.tree.select(root.id, {
						// #23438 toggleOnSelect should be true when switching sections
						// and landing on a folder.
						toggleOnSelect: true,
						animate: false
					})
				}
			}

			this.trigger('tree:rendered')
			this.clearLoadingDots()
		})
	},

	/**
	 * @param {XMLHttpRequest} [stream]
	 *
	 * @return {Number} The ID of the current organization.  Doesn't rely on
	 * globals.orgId because it may not have been set yet when first loading
	 * the application
	 */
	getOrgId: function(stream = this.stream) {
		const [orgId] = (stream && stream.headers) ? _.split(stream.headers[ORG_HEADER_NAME.toLowerCase()], '=') : []
		return orgId
	},

	move: function(node, newParent, oldParent, newIndex, oldIndex) {
		var options = {
			mode: 'move',
			section: this.options.section,
			id: node.id,
			parentId: newParent && newParent.id,
			orgId: globals.orgId,
			oldOrgId: globals.orgId,
			index: newIndex
		}
		paste(options, function(ids) {
			// if the root of the tree changed, fire an event
			if (oldIndex == 0 || newIndex == 0) {
				Backbone.trigger('change:root', options, ids)
			}
		})
	},

	/**
	 * If a gardener is used outside of a section this function needs overridden.
	 *
	 * @param {Object} model
	 * @param {String} model.domainType the domain of the object being acted on
	 *
	 * @returns {boolean} true if this gardener is interested in events that
	 * happened in the given domainType as compared to the current section's domain type
	 */
	interested: function(model) {
		var interestedDomainType = _.get(metadata[this.options.section], 'domainType')
		return model.domainType == interestedDomainType
	},

	_ignoreTreeChange(model) {
		return !this.interested(model) ||
			// If a view has a gardener that hasn't rendered the tree yet
			// this must be a noop (#42753)
			!this.tree
	},

	_addDomainObject: function(model, relativeId) {

		if (this._ignoreTreeChange(model)) return

		// If we're adding a model that has an organizationId, make sure that it matches the
		// org of the tree we're displaying
		var orgId = model.get('organizationId')
		if (!_.isNil(orgId) && !_.isNil(this.options.orgId) && orgId != this.options.orgId) {
			return
		}

		this.add(model, relativeId)

		var parent = this.tree.parent(model.id)
		// if we're adding the first item to the tree
		if (!this.tree.selected() ||
				// or the parent of the added node isn't selected
				_.get(parent, 'id') != this.tree.selected().id) {
			this.lastAddedSibling = model
		}
	},

	/**
	 * Adds a model relative to another node, as long as the node hasn't already
	 * been added
	 */
	add: function(model, relativeId) {
		if (!this.tree.get(model.id)) {
			var position = this.getAddPosition(model, relativeId)
			var attributes = model.attributes || model
			var index = position.index
			if (!this.forest && !position.parent) {
				// tree doesn't like to have an index defined when there's no
				// parent and it's not a forest tree
				index = undefined
			}
			_.each(this.nodeTransforms, transformFn => transformFn(attributes))
			siprNodeTransform(attributes)
			this.tree.add(attributes, position.parentId, index)
		}
	},

	/**
	 * Based on the type of the selected item and the type of an item about
	 * to be newly created, returns whether the newly created item should be
	 * positioned as a CHILD or SIBLING or ROOT
	 */
	getAddType: newFolderAddType,

	/**
	 * Retrieves the position where a node should be added in the tree based
	 * on the type of object it and the ID it should be added relative to.
	 * returns an object like this: { parentId: 1, index: 0 }
	 * @param {String} [mode] 'copy' or 'move' (must be specified when moving
	 * Otherwise, can be ommitted)
	 */
	getAddPosition: function(model, relativeId, mode) {
		model = unwrapModel(model)
		//#38140 simplify the add process for classified content
		// #18702 classified objects that are created relative to unclassified
		// objects should be under the root level of the tree, rather than the
		// normal behavior (since users should never be able to add classified
		// children to a non-classifiedparent)
		if (config.replicationInputEnabled && !_.isNil(relativeId)) {
			let isClassifiedNode = _.isNil(model.syncId)
			let parentAttributes = this.tree.get(relativeId)
			if (parentAttributes) {
				let isUnclassifiedParent = !_.isNil(parentAttributes.syncId)

				//#38217 carve exception for converted docking nodes
				let isConvertedDockingNode = parentAttributes.originallyDocked
				if (isUnclassifiedParent && isClassifiedNode && !isConvertedDockingNode){
					relativeId = null
				}
			}
		}

		// if adding to the top level
		if (relativeId == null) {
			var index
			// if there's stuff in the tree
			if (this.tree.get()) {
				// if the only thing is a transient, the index should be 0
				if (this.tree.get().length == 1 &&
					this.tree.get()[0].id == this.tree.options.transientId) {
					index = 0
				// if there are non-transients in there
				} else {
					// add to the end, not including any transient nodes though
					index = _.filter(this.tree.get(), n => n.id > 0).length
				}
			} else {
				index = 0
			}
			return { index: index }
		}
		var relative = this.tree.get(relativeId)
		var addType = this.getAddType(relative, model)
		if (addType == ROOT) {
			return {parentId: null, index: null}
		} else if (addType === Gardener.SIBLING && this.lastAddedSibling) {
			return this._getNextSiblingPosition(model, this.lastAddedSibling.id)
		} else {
			if (addType === CHILD) {
				return { parentId: relativeId }
			} else {
				return this._getNextSiblingPosition(model, relativeId)
			}
		}
	},

	/**
	 * Returns the next sibling position when new items should be added to the
	 * tree in the sibling position, e.g. { parentId: 1, index: 3 }. The next
	 * sibling is the node after the currently selected node in the tree.
	 * @param {Backbone.Model|Object} model the reference item for which we
	 * are determining the next sibling position
	 * @param {String} [mode] 'copy' or 'move' (must be specified when moving
	 * Otherwise, can be omitted)
	 */
	_getNextSiblingPosition: function(model, id) {
		model = unwrapModel(model)
		var parent = id && this.tree.parent(id)
		var siblings = parent ? this.tree.children(parent) : this.tree.get()
		let nodeIndex = _.findIndex(siblings, function(sibling) {
			return sibling.id == model.id
		})
		var siblingIndex = _.findIndex(siblings, function(sibling) {
			return sibling.id == id
		})
		let index = siblingIndex
		if (nodeIndex == -1 ||
			// #31297 add one so the node moves below the sibling, but only if
			// the node came after the sibling when moving
			nodeIndex > siblingIndex ||
			// #31510 always add one when not moving
			this.options.mode != 'move') {
			index++
		}
		return {
			// parent may be null in compound trees
			parentId: parent && parent.id,
			index
		}
	},

	_editDomainObject: function(model, options = {}) {
		if (this._ignoreTreeChange(model)) return

		// #55972 Don't update the tree if we're doing a whole batch of edits;
		// it will perform better if we do them all at once, which we can do
		// when the `edit:domain-objects` event is fired
		if (!options?.batch) {
			this._applyTreeEdit(model)
		}
	},

	_editDomainObjects (models) {
		this._applyTreeEdit(models.filter(m => !this._ignoreTreeChange(m)))
	},

	_applyTreeEdit (models, options = {}) {
		if (!models) {
			return
		}

		if (!_.isArray(models)) {
			models = [ models ]
		}

		models = models.map(m => {
			m = unwrapModel(m)
			siprNodeTransform(m)
			return m
		})

		this.tree.edit(models, options)
	},

	_deleteDomainObject: function(model) {
		if (this._ignoreTreeChange(model)) return

		this.tree.removeNode(model)
	},

	_moveDomainObject: function(options) {
		if (!this.interested({domainType: options.domainType})) return

		if (options.orgId == globals.orgId) {
			this.tree.move(options.id, options.parentId, options.index)
			return
		}

		if (options.orgId) {
			// moving to end of forest
			if (options.orgId == globals.orgId) {
				this.tree.move(options.id)
			// moving to different org
			} else {
				// make sure the tree knows the node isn't in the old tree
				// anymore
				this.tree.removeNode(options.id)
			}
		}
	},


	/**
	 * Handles copied domain objects
	 * @param options the options used to perform the paste
	 * @ids a hash of old IDs to new IDs
	 */
	_copyDomainObject: function(options, ids) {
		var domainType = metadata[options.section].domainType
		if (!this.interested({domainType})) return

		// transforms a newly copied node
		function copyTransform(node) {
			// fix the ID of the duplicated node
			node.id = ids[node.id]
			// newly copied nodes don't have any data so they're gray
			node.color = 'gray'
			if (options.label) {
				// The new copy may have a different label; for example, a
				// templated copy may have a prefix and/or suffix added
				node.label = options.label
			}
			return node
		}

		if (options.parentId && this.tree.get(options.parentId)) {
			this.tree.copy(options.id, options.parentId, options.index, copyTransform)
			return
		}

		if (options.orgId) {
			// copying to top level of forest
			if (options.orgId == globals.orgId) {
				this.tree.copy(options.id, null, options.index, copyTransform)
			}
			// note if we copied to a different org there's nothing to worry
			// about here, unlike the case with move
		}
	},

	search: function(text) {
		this.tree.search(text)
	},

	/*
	 * Default implementation just invokes collapseAll on the tree
	 * Can be overridden by specific gardeners
	 */
	collapseAll: function () {
		this.tree.collapseAll()
	},

	/*
	 * Default implementation just invokes expandAll on the tree
	 * Can be overridden by specific gardeners
	 */
	expandAll: function () {
		this.tree.expandAll()
	},

	// toggleCollapse can be called programmatically (forgo event trigger to prevent loop)
	// or via clicking the collapse/expand button (migrated from nav-bottom-toolbar.js)
	toggleCollapse: function(isCollapsed) {
		if (isCollapsed.type == 'click') {
			this.$('.icon-collapse-all, .nav-collapse-text').toggleClass('is-not-displayed')
			this.$('.icon-expand-all, .nav-expand-text').toggleClass('is-not-displayed')
			this.toggleExpandAll(isCollapsed)
			this.trigger('toggleCollapse')
		} else {
			this.$('.icon-collapse-all, .nav-collapse-text').toggleClass('is-not-displayed', isCollapsed)
			this.$('.icon-expand-all, .nav-expand-text').toggleClass('is-not-displayed', !isCollapsed)
			this.collapsed = isCollapsed
		}
	},

	/**
	 * Toggles between the tree's expanded and collapsed states based on user
	 * input (migrated from nav.js)
	 */
	toggleExpandAll: function (e) {
		// don't let clicking on expand all in org pane make org pane hide since
		// expand all button for nav pane is beneath it
		e && e.stopPropagation()

		// if this method was invoked in response to something the user did,
		// we'll need to expand or collapse the tree, but let's wrap that in
		// process.nextTick so the expand all and collapse all buttons change
		// first before tree animations start.  This will make the UI feel more
		// responsive in case the expand all or collapse all is on a huge tree
		// and takes some time
		process.nextTick(function() {
			// clear search results
			if (this.finder.text()) {
				this.finder.clear()
				this.tree.search(null)
			}

			this.collapsed ? this.expandAll() : this.collapseAll()
			this.collapsed = !this.collapsed
		}.bind(this))
	},

	// needed so the initiatives-gardener can override this behavior
	removeTransient: function() {
		let transient = _.result(this.tree, 'getTransient')
		if (transient && transient.transientDndStopListener) {
			//clean up so node doesn't leak mem
			this.tree.removeListener('dndstop', transient.transientDndStopListener)
		}

		this.tree && this.tree.removeTransient({animate: false})
		delete this.lastAddedSibling
	},

	clearSelection: function () {
		this.tree.select(null)
		this.trigger('clear')
	},

	resize: function() {
		this.tree && this.tree.resize()
	},

	remove: function () {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.tree, this.stream], 'removeAllListeners')
		_.invokeMap([this.tree, this.finder, this.topLevelNode], 'remove')
	}

})

export default Gardener
export { SIBLING }
export { CHILD }
export { ROOT }

Gardener.__filename__ = 'components/gardener.js'
